import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Lead, Me, Meta } from '@shared/types';
import { queueKpis, stageVocabulary, type QueueKpis } from '@shared/kpi';
import { holidayOverrides } from '@shared/holidays';
import { formatDateTime, formatHours } from '@shared/dates';
import { useBulk, useLeads, useLiveUpdates, useMeta, useQueueExits, useStages, useToast } from './store';
import { applyFilters, DEFAULT_FILTERS, distinctSources, groupLeads, inPeriod, PERIOD_LABELS, type Filters, type Period } from './filter';
import { Icons, Spinner, useIsMobile, Modal } from './components/ui';
import { GroupedTable, type Selection } from './components/GroupedTable';
import { MobileCardList, scrollToGroupTabs } from './components/MobileCards';
import { useMinuteClock } from './clock';
import { useRoute, type Tab } from './route';
import { StatsPage } from './components/Stats';
import { TasksPage } from './components/Tasks';
import { Kanban } from './components/Kanban';
import { LeadDetailPanel } from './components/LeadDetail';
import { SettingsPage } from './components/Settings';
import { NotifyControls } from './components/Notify';
import { PillLabel, StageFilterDropdown } from './components/StagePill';

type View = 'table' | 'kanban';

export function App({ me, signOut, userLabel, onSwitchTenant }: { me: Me; signOut?: () => void; userLabel?: string; onSwitchTenant: (id: string) => void }) {
  const mobile = useIsMobile();
  const { data: meta, isLoading: metaLoading, error: metaError } = useMeta();
  const { data: leads = [], isLoading: leadsLoading } = useLeads();
  const connected = useLiveUpdates();
  const { route, setTab, openLead, closeLead, follow } = useRoute();
  const { tab, leadId: openId } = route;
  const [view, setView] = useState<View>('table');
  const [filters, setFilters] = useState<Filters>({ ...DEFAULT_FILTERS });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { stageName } = useStages();
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const now = useMinuteClock();

  const filtered = useMemo(() => applyFilters(leads, filters, stageName), [leads, filters, stageName]);
  const grouped = useMemo(() => groupLeads(filtered, meta?.groups ?? [], stageName), [filtered, meta?.groups, stageName]);
  const { data: exits = {} } = useQueueExits();
  // The period row follows the date filter (default: last 30 days); neither row follows the search or the other filters.
  const periodLeads = useMemo(() => leads.filter((l) => inPeriod(l, filters.period, now)), [leads, filters.period, now]);
  const settings = meta?.tenant.settings;
  // Holidays replace the weekly hours on their dates (closed / Friday hours), per the client's rules.
  const holidayHours = useMemo(() => (settings ? holidayOverrides(meta?.holidays ?? [], settings.holidayRules, settings.businessHours) : undefined), [meta?.holidays, settings]);
  const k = useMemo(() => queueKpis({ periodLeads, groups: meta?.groups ?? [], stageName, exits, now, hours: settings?.businessHours, holidays: holidayHours }),
    [periodLeads, meta?.groups, stageName, exits, now, settings?.businessHours, holidayHours]);
  /** A KPI tile opens its group's tab and brings the tab bar to the top. */
  const openGroup = useCallback((id: string) => { setActiveGroup(id); requestAnimationFrame(() => scrollToGroupTabs(true)); }, []);

  const selection: Selection = useMemo(() => ({
    selected,
    toggle: (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }),
    setMany: (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; }),
  }), [selected]);

  // A notification tapped while the app is open: the service worker passes its address along.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => { if (e.data?.type === 'piper-open' && typeof e.data.url === 'string') follow(e.data.url); };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [follow]);
  const switchTenant = (id: string) => { onSwitchTenant(id); setTab('home'); };

  if (metaLoading || leadsLoading) return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  if (metaError || !meta) return <div className="p-6 text-danger">טעינת ההגדרות נכשלה: {String((metaError as Error)?.message ?? '')}</div>;

  const content = tab === 'settings' ? <SettingsPage meta={meta} me={me} onSwitchTenant={switchTenant} />
    : tab === 'performance' ? <StatsPage leads={leads} meta={meta} />
    : tab === 'tasks' ? <TasksPage onOpenLead={openLead} />
    : view === 'kanban' && !mobile ? <Kanban groups={grouped} pipelines={meta.pipelines} onOpen={openLead} />
    : mobile ? <MobileCardList groups={grouped} onOpen={openLead} active={activeGroup} onSelect={setActiveGroup} filterKey={JSON.stringify(filters)} />
    : <GroupedTable groups={grouped} selection={selection} onOpen={openLead} active={activeGroup} onSelect={setActiveGroup} filterKey={JSON.stringify(filters)} />;

  const shellProps = { meta, me, tab, setTab, view, setView, filters, setFilters, leads, connected, k, openGroup, signOut, userLabel, selection, clearSelection: () => setSelected(new Set()), switchTenant };
  return (
    <>
      {mobile ? <MobileShell {...shellProps}>{content}</MobileShell> : <DesktopShell {...shellProps}>{content}</DesktopShell>}
      <LeadDetailPanel leadId={openId} onClose={closeLead} />
    </>
  );
}

type ShellProps = {
  meta: Meta; me: Me; switchTenant: (id: string) => void; tab: Tab; setTab: (t: Tab) => void; view: View; setView: (v: View) => void; filters: Filters; setFilters: (f: Filters) => void;
  leads: Lead[]; connected: boolean; k: QueueKpis; openGroup: (groupId: string) => void; signOut?: () => void; userLabel?: string; selection: Selection; clearSelection: () => void; children: ReactNode;
};

function NotInstalledBanner({ meta, setTab }: { meta: Meta; setTab: (t: Tab) => void }) {
  if (meta.sync.installed && meta.sync.tokenOk) return null;
  return (
    <div className="mx-4 mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
      {!meta.sync.installed ? 'האפליקציה עדיין לא מותקנת ב-CRM.' : 'החיבור ל-CRM נשבר ויש להתקין מחדש.'}{' '}
      {meta.me.role === 'admin' ? <button className="underline" onClick={() => setTab('settings')}>להגדרות</button> : 'פנה למנהל.'}
    </div>
  );
}

function DesktopShell(p: ShellProps) {
  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-panel px-4 py-2">
        <Wordmark />
        <TenantSwitch me={p.me} current={p.meta.tenant.locationId} onSwitch={p.switchTenant} />
        <span className="ms-auto flex items-center gap-2 text-xs text-ink-3">
          <span className={`inline-block h-2 w-2 rounded-full ${p.connected ? 'bg-green-500' : 'bg-ink-3'}`} aria-hidden="true" />
          <span title="שינויים מ-CRM מגיעים בזמן אמת דרך webhooks; בנוסף רצה סריקה מלאה פעם בשעה כרשת ביטחון">
            {p.connected ? 'עדכונים חיים' : 'מתחבר…'}
            {p.meta.sync.lastFullSync && <> · סריקה מלאה אחרונה <span className="ltr">{formatDateTime(p.meta.sync.lastFullSync)}</span></>}
            {p.meta.sync.running && ' (סורק…)'}
          </span>
          <NotifyControls />
        </span>
        <nav className="flex items-center gap-1">
          <NavBtn active={p.tab === 'home'} onClick={() => p.setTab('home')}><Icons.Home size={16} /> לידים</NavBtn>
          <NavBtn active={p.tab === 'tasks'} onClick={() => p.setTab('tasks')}><Icons.TaskCal size={15} /> משימות</NavBtn>
          <NavBtn active={p.tab === 'performance'} onClick={() => p.setTab('performance')}><Icons.Chart size={16} /> ביצועים</NavBtn>
          <NavBtn active={p.tab === 'settings'} onClick={() => p.setTab('settings')}><Icons.Settings size={16} /> הגדרות</NavBtn>
        </nav>
        <span className="flex items-center gap-2 border-s border-line ps-3 text-sm">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft font-semibold text-accent">{(p.userLabel ?? '?').slice(0, 1)}</span>
          <span className="max-w-32 truncate">{p.userLabel}</span>
          {p.signOut && <button className="btn btn-icon" title="התנתק" aria-label="התנתק" onClick={p.signOut}><Icons.Logout size={16} /></button>}
        </span>
      </header>
      <NotInstalledBanner meta={p.meta} setTab={p.setTab} />
      {p.tab === 'home' && (
        <>
          <div className="px-4 pt-4"><KpiTiles k={p.k} period={p.filters.period} onOpenGroup={p.openGroup} /></div>
          <div className="px-4"><Toolbar {...p} /></div>
          <BulkBar {...p} />
        </>
      )}
      <main className="flex-1 p-4">{p.children}</main>
    </div>
  );
}

function MobileShell(p: ShellProps) {
  const [q, setQ] = useState(p.filters.q);
  useEffect(() => { const t = setTimeout(() => p.setFilters({ ...p.filters, q }), 120); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex min-h-full flex-col" style={{ paddingBottom: 'calc(64px + env(safe-area-inset-bottom))' }}>
      {p.tab === 'home' ? (
        <>
          <header className="bg-gradient-to-l from-accent to-indigo-600 px-4 py-3 text-white" style={{ paddingTop: 'calc(12px + env(safe-area-inset-top))' }}>
            <div className="flex items-center gap-3">
              <Wordmark light />
              <p className="min-w-0 flex-1 truncate text-sm text-white/80">כל הלידים של {p.meta.tenant.name}, במקום אחד</p>
              <span className={`h-2 w-2 shrink-0 rounded-full ${p.connected ? 'bg-green-300' : 'bg-white/40'}`} title={p.connected ? 'עדכונים חיים' : 'מתחבר…'} />
              <NotifyControls light />
            </div>
            {p.me.tenants.length > 1 && <div className="mt-2"><TenantSwitch me={p.me} current={p.meta.tenant.locationId} onSwitch={p.switchTenant} light /></div>}
          </header>
          <NotInstalledBanner meta={p.meta} setTab={p.setTab} />
          <div className="p-3"><KpiTiles k={p.k} period={p.filters.period} onOpenGroup={p.openGroup} /></div>
          <div className="px-3 pb-2">
            <div className="relative"><input className="input ps-10" placeholder="חיפוש לפי שם, טלפון או אימייל" value={q} onChange={(e) => setQ(e.target.value)} aria-label="חיפוש"
              onFocus={(e) => {
                // Pin the search box to the top so the results are visible while typing (the hero/KPIs scroll away).
                const top = e.currentTarget.getBoundingClientRect().top + window.scrollY - 8;
                window.scrollTo({ top, behavior: 'auto' }); // instant: the keyboard opening on phones would fight a smooth scroll
              }} /><Icons.Search size={18} className="pointer-events-none absolute inset-y-0 my-auto start-3 text-ink-3" /></div>
            <ChipRow {...p} />
          </div>
        </>
      ) : (
        <header className="border-b border-line bg-panel px-4 py-3" style={{ paddingTop: 'calc(12px + env(safe-area-inset-top))' }}><Wordmark /></header>
      )}
      <main className="flex-1 py-2">{p.children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-line bg-panel" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {([['home', 'בית', <Icons.Home key="h" />], ['tasks', 'משימות', <Icons.TaskCal key="t" size={18} />], ['performance', 'ביצועים', <Icons.Chart key="c" />], ['settings', 'הגדרות', <Icons.Settings key="s" />]] as const).map(([t, label, icon]) => (
          <button key={t} className={`flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs ${p.tab === t ? 'text-accent' : 'text-ink-2'}`} onClick={() => p.setTab(t)} aria-current={p.tab === t ? 'page' : undefined}>{icon}{label}</button>
        ))}
      </nav>
      {p.signOut && p.tab === 'settings' && <div className="px-4 pb-4"><button className="btn w-full" onClick={p.signOut}><Icons.Logout size={16} /> התנתק</button></div>}
    </div>
  );
}

/** Shown only when the person belongs to more than one client. */
function TenantSwitch({ me, current, onSwitch, light }: { me: Me; current: string; onSwitch: (id: string) => void; light?: boolean }) {
  const cur = me.tenants.find((t) => t.locationId === current);
  if (me.tenants.length <= 1) return <span className={`text-sm font-medium ${light ? 'text-white' : 'text-ink-2'}`}>{cur?.name}</span>;
  return (
    <select className={`input h-9 min-h-0 w-auto max-w-56 ${light ? 'border-white/40 bg-white/15 text-white' : ''}`} value={current} onChange={(e) => onSwitch(e.target.value)} aria-label="בחר לקוח">
      {me.tenants.map((t) => <option key={t.locationId} value={t.locationId} className="text-ink">{t.name}</option>)}
    </select>
  );
}

function Wordmark({ light }: { light?: boolean }) {
  return <span className={`flex items-center gap-2 text-lg font-bold ${light ? 'text-white' : 'text-ink'}`}><span className={`flex h-7 w-7 items-center justify-center rounded-lg ${light ? 'bg-white text-accent' : 'bg-accent text-white'}`}>P</span>Piper</span>;
}
function NavBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button className={`btn border-transparent ${active ? 'bg-accent-soft text-accent' : ''}`} onClick={onClick} aria-current={active ? 'page' : undefined}>{children}</button>;
}

const PERIOD_PHRASE: Record<Period, string> = { all: 'כל הזמן', '24h': '24 שעות', '7d': '7 ימים', '30d': '30 ימים' };

/**
 * Both rows cover leads that arrived in the date filter's window.
 * Row 1: how many still have no first contact (status חדש), and their average age in the queue.
 * Row 2: how long leads waited in the queue (including those already handled), and how many now wait for another attempt.
 * Waits are business hours. On mobile a tile opens its group's tab.
 */
function KpiTiles({ k, period, onOpenGroup }: { k: QueueKpis; period: Period; onOpenGroup?: (groupId: string) => void }) {
  const open = (id: string | null | undefined) => (id && onOpenGroup ? () => onOpenGroup(id) : undefined);
  const range = PERIOD_PHRASE[period];
  const sample = k.period.sample < k.period.arrived ? `לפי ${k.period.sample.toLocaleString('he-IL')} מתוך ${k.period.arrived.toLocaleString('he-IL')} לידים` : undefined;
  return (
    <div className="grid grid-cols-2 gap-2 desk:grid-cols-4 desk:gap-3">
      <KpiCard icon={<Icons.Users size={18} />} label={`ממתינים לקשר ראשוני (${range})`} value={k.queue.count.toLocaleString('he-IL')} onClick={open(k.queue.groupId)} />
      <KpiCard icon={<Icons.Clock size={18} />} label={`גיל ממוצע בתור (${range})`} value={k.queue.avgMs === null ? 'אין ממתינים' : formatHours(k.queue.avgMs)}
        title="ממוצע, בשעות הפעילות, של הלידים שעדיין בסטטוס חדש — מאז שהגיעו" onClick={open(k.queue.groupId)} />
      <KpiCard icon={<Icons.Hourglass size={18} />} label={`זמן המתנה בתור (${range})`} value={k.period.avgMs === null ? '—' : formatHours(k.period.avgMs)} sub={sample}
        title="ממוצע, בשעות הפעילות, מהגעת הליד עד שהסטטוס שלו השתנה מחדש (או עד עכשיו)" onClick={open(k.queue.groupId)} />
      {k.retry && <KpiCard icon={<Icons.Refresh size={18} />} label={`ממתינים לניסיונות נוספים (${range})`} value={k.retry.count.toLocaleString('he-IL')} onClick={open(k.retry.groupId)} />}
    </div>
  );
}
function KpiCard({ label, value, icon, onClick, sub, title }: { label: string; value: string; icon?: ReactNode; onClick?: () => void; sub?: string; title?: string }) {
  // "29:42 שעות" → a big number and a small unit, so a duration fits a half-width tile on one line
  const m = value.match(/^([\d:,.]+)\s+(.+)$/);
  const body = (
    <>
      {icon && <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">{icon}</span>}
      <div className="min-w-0 text-start">
        <div className="text-xs leading-snug text-ink-3">{label}</div>
        <div className="whitespace-nowrap text-xl font-bold leading-tight">
          {m ? <><span className="tabular-nums">{m[1]}</span> <span className="text-sm font-semibold text-ink-2">{m[2]}</span></> : value}
        </div>
        {sub && <div className="text-[11px] leading-tight text-ink-3">{sub}</div>}
      </div>
    </>
  );
  return onClick
    ? <button type="button" title={title} className="card flex items-center gap-3 p-3 transition-colors hover:bg-page active:scale-[.99]" onClick={onClick}>{body}</button>
    : <div className="card flex items-center gap-3 p-3" title={title}>{body}</div>;
}

function Toolbar(p: ShellProps) {
  const [q, setQ] = useState(p.filters.q);
  useEffect(() => { const t = setTimeout(() => p.setFilters({ ...p.filters, q }), 80); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const sources = useMemo(() => distinctSources(p.leads), [p.leads]);
  const stages = stageVocabulary(p.meta.pipelines);
  const set = (patch: Partial<Filters>) => p.setFilters({ ...p.filters, ...patch });
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <div className="relative min-w-64 flex-1"><input className="input ps-9" placeholder="חיפוש לפי שם, טלפון או אימייל…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="חיפוש" /><Icons.Search size={16} className="pointer-events-none absolute inset-y-0 my-auto start-3 text-ink-3" /></div>
      <StageFilterDropdown value={p.filters.stageName} stages={stages} onChange={(stageName) => set({ stageName })} />
      <select className="input w-44" value={p.filters.source} onChange={(e) => set({ source: e.target.value })} aria-label="מקור"><option value="">כל המקורות</option>{sources.map((s) => <option key={s} value={s}>{s}</option>)}</select>
      <select className="input w-40" value={p.filters.owner} onChange={(e) => set({ owner: e.target.value })} aria-label="אחראי"><option value="">כל האחראים</option><option value="__none__">ללא אחראי</option>{p.meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
      <select className="input w-40" value={p.filters.period} onChange={(e) => set({ period: e.target.value as Period })} aria-label="תקופה"><option value="all">כל הזמן</option><option value="24h">24 שעות אחרונות</option><option value="7d">7 ימים אחרונים</option><option value="30d">30 ימים אחרונים</option></select>
      <select className="input w-40" value={p.filters.pipelineId} onChange={(e) => set({ pipelineId: e.target.value })} aria-label="פייפליין"><option value="">שני הפייפליינים</option>{p.meta.pipelines.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
      {(p.filters.q || p.filters.stageName || p.filters.source || p.filters.owner || p.filters.period !== DEFAULT_FILTERS.period || p.filters.pipelineId) && <button className="btn btn-sm" onClick={() => { setQ(''); p.setFilters({ ...DEFAULT_FILTERS }); }}>נקה</button>}
      <div className="ms-auto flex rounded-lg border border-line p-0.5" role="group" aria-label="תצוגה">
        <button className={`btn btn-sm border-0 ${p.view === 'table' ? 'bg-accent-soft text-accent' : ''}`} onClick={() => p.setView('table')} aria-pressed={p.view === 'table'}><Icons.Rows size={14} /> טבלה</button>
        <button className={`btn btn-sm border-0 ${p.view === 'kanban' ? 'bg-accent-soft text-accent' : ''}`} onClick={() => p.setView('kanban')} aria-pressed={p.view === 'kanban'}><Icons.Grid size={14} /> כרטיסיות</button>
      </div>
    </div>
  );
}

function ChipRow(p: ShellProps) {
  const set = (patch: Partial<Filters>) => p.setFilters({ ...p.filters, ...patch });
  const stages = stageVocabulary(p.meta.pipelines);
  const sources = distinctSources(p.leads);
  const [open, setOpen] = useState<null | 'stage' | 'source' | 'owner'>(null);
  const chip = (label: string, active: boolean, onClick: () => void) => <button className={`rounded-full border px-3 py-1.5 text-sm ${active ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-panel'}`} onClick={onClick}>{label}</button>;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="me-0.5 text-sm font-medium text-ink-2">סינון</span>
      {(['all', '24h', '7d', '30d'] as Period[]).map((per) => <span key={per} className="contents">{chip(PERIOD_LABELS[per], p.filters.period === per, () => set({ period: per }))}</span>)}
      {chip(p.filters.stageName || 'סטטוס', !!p.filters.stageName, () => setOpen('stage'))}
      {chip(p.filters.source || 'מקור', !!p.filters.source, () => setOpen('source'))}
      {chip(p.filters.owner ? (p.filters.owner === '__none__' ? 'ללא אחראי' : p.meta.users.find((u) => u.id === p.filters.owner)?.name ?? 'אחראי') : 'אחראי', !!p.filters.owner, () => setOpen('owner'))}
      <Modal open={open !== null} onClose={() => setOpen(null)} title={open === 'stage' ? 'סטטוס' : open === 'source' ? 'מקור' : 'אחראי'}>
        <div className="grid gap-1">
          <button className="btn justify-start" onClick={() => { set(open === 'stage' ? { stageName: '' } : open === 'source' ? { source: '' } : { owner: '' }); setOpen(null); }}>הכל</button>
          {open === 'stage' && stages.map((s) => <button key={s} className="btn justify-start" onClick={() => { set({ stageName: s }); setOpen(null); }}><PillLabel name={s} /></button>)}
          {open === 'source' && sources.map((s) => <button key={s} className="btn justify-start" onClick={() => { set({ source: s }); setOpen(null); }}>{s}</button>)}
          {open === 'owner' && [{ id: '__none__', name: 'ללא אחראי' }, ...p.meta.users].map((u) => <button key={u.id} className="btn justify-start" onClick={() => { set({ owner: u.id }); setOpen(null); }}>{u.name}</button>)}
        </div>
      </Modal>
    </div>
  );
}

function BulkBar(p: ShellProps) {
  const bulk = useBulk();
  const toast = useToast();
  const n = p.selection.selected.size;
  const [stage, setStage] = useState('');
  const [owner, setOwner] = useState('');
  const run = useCallback((change: { stageName?: string; assignedTo?: string | null }) => {
    bulk.mutate({ ids: [...p.selection.selected], ...change }, { onSuccess: () => { p.clearSelection(); setStage(''); setOwner(''); } });
  }, [bulk, p]);
  if (n === 0) return null;
  return (
    <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-accent bg-accent-soft/50 px-3 py-2 text-sm">
      <span className="font-semibold">{n} נבחרו</span>
      <select className="input w-44" value={stage} onChange={(e) => setStage(e.target.value)} aria-label="שנה סטטוס"><option value="">שנה סטטוס ל…</option>{stageVocabulary(p.meta.pipelines).map((s) => <option key={s} value={s}>{s}</option>)}</select>
      <select className="input w-40" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="שייך לאחראי"><option value="">שייך לאחראי…</option><option value="__none__">ללא אחראי</option>{p.meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
      <button className="btn btn-primary btn-sm" disabled={(!stage && !owner) || bulk.isPending} onClick={() => { if (!stage && !owner) return toast('בחר סטטוס או אחראי'); run({ ...(stage ? { stageName: stage } : {}), ...(owner ? { assignedTo: owner === '__none__' ? null : owner } : {}) }); }}>{bulk.isPending ? <Spinner /> : 'החל'}</button>
      <button className="btn btn-sm ms-auto" onClick={p.clearSelection}>בטל בחירה</button>
    </div>
  );
}


